import { useMemo } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import type { MetricSnapshot } from '../../types'
import type { TimePeriod } from './useMetricsData'

interface ResponseTimeChartProps {
  timeSeries: MetricSnapshot[]
  period: TimePeriod
  loading?: boolean
}

function formatTimestamp(timestamp: string, period: TimePeriod): string {
  const date = new Date(timestamp)
  if (period === '1h' || period === '6h' || period === '24h') {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatDuration(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`
  return `${ms.toFixed(0)}ms`
}

const PERCENTILE_COLORS = {
  p50: '#22c55e', // green
  p95: '#f59e0b', // amber
  p99: '#ef4444', // red
}

export function ResponseTimeChart({ timeSeries, period, loading = false }: ResponseTimeChartProps) {
  const chartData = useMemo(() => {
    return timeSeries.map(snapshot => ({
      timestamp: snapshot.created_at,
      p50: snapshot.metrics['http_request_duration_p50'] ?? null,
      p95: snapshot.metrics['http_request_duration_p95'] ?? null,
      p99: snapshot.metrics['http_request_duration_p99'] ?? null,
    }))
  }, [timeSeries])

  // Check if we have any response time data
  const hasData = chartData.some(d => d.p50 !== null || d.p95 !== null || d.p99 !== null)

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-slate-900">Response Time</h3>
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-500" />
        </div>
      ) : !hasData ? (
        <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
          No response time data available
        </div>
      ) : (
        <div style={{ width: '100%', height: 192 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis
                dataKey="timestamp"
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                tickFormatter={value => formatTimestamp(value, period)}
                axisLine={{ stroke: '#e2e8f0' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                tickFormatter={formatDuration}
                axisLine={false}
                tickLine={false}
                width={55}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: 12,
                }}
                labelFormatter={value => {
                  const date = new Date(value)
                  return date.toLocaleString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                }}
                formatter={(value, name) => [
                  typeof value === 'number' ? formatDuration(value) : '-',
                  String(name).toUpperCase(),
                ]}
              />
              <Legend
                verticalAlign="top"
                align="right"
                iconType="line"
                iconSize={12}
                wrapperStyle={{ fontSize: 11, paddingBottom: 8 }}
                formatter={(value: string) => (
                  <span className="text-slate-500">{value.toUpperCase()}</span>
                )}
              />
              <Line
                type="monotone"
                dataKey="p50"
                name="p50"
                stroke={PERCENTILE_COLORS.p50}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="p95"
                name="p95"
                stroke={PERCENTILE_COLORS.p95}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
              <Line
                type="monotone"
                dataKey="p99"
                name="p99"
                stroke={PERCENTILE_COLORS.p99}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
